import { useEffect, useState } from "react";
import { ImagePlus, Loader2, Save, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { refreshSiteContent, useSiteContent } from "../../lib/site";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import MediaPicker from "../MediaPicker";

const ROLE_TR = { admin: "Yönetici", buyer: "Mağaza", supplier: "Tedarikçi" };

export default function ProfileTab() {
    const [me, setMe] = useState(null);
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const site = useSiteContent();
    const [contact, setContact] = useState(null);
    const [busyC, setBusyC] = useState(false);

    useEffect(() => {
        api.get("/auth/me").then((r) => {
            setMe(r.data);
            setForm({ name: r.data.name || "", phone: r.data.phone || "", picture: r.data.picture || "" });
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (contact === null && Object.keys(site).length > 0) {
            setContact({
                contact_address: site.contact_address || "",
                contact_phone: site.contact_phone || "",
                contact_email: site.contact_email || "",
                site_whatsapp: site.site_whatsapp || "",
            });
        }
    }, [site]);

    const saveContact = async () => {
        setBusyC(true);
        try {
            await api.put("/admin/content", { updates: contact });
            await refreshSiteContent();
            toast.success("İletişim bilgileri güncellendi — İletişim sayfasında anında yayında");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusyC(false);
        }
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Ad soyad zorunludur");
        setBusy(true);
        try {
            await api.put("/auth/profile", form);
            toast.success("Profil bilgileriniz güncellendi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    if (!me || !form) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;

    return (
        <div className="max-w-2xl space-y-6" data-testid="profile-tab">
            <div>
                <h2 className="font-display text-xl font-bold">Profil Bilgilerim</h2>
                <p className="mt-1 text-sm text-muted-foreground">Hesap bilgilerinizi görüntüleyin ve güncelleyin. Değişiklikler anında geçerli olur.</p>
            </div>

            <div className="space-y-4 rounded-2xl border bg-card p-6 shadow-sm">
                <div className="flex items-center gap-4">
                    {form.picture ? (
                        <img src={form.picture} alt="Profil fotoğrafı" className="h-20 w-20 rounded-full border object-cover" data-testid="profile-avatar-preview" />
                    ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-full border bg-muted">
                            <User className="h-8 w-8 text-muted-foreground" />
                        </div>
                    )}
                    <div>
                        <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="profile-pick-image">
                            <ImagePlus className="mr-2 h-4 w-4" /> Fotoğraf Seç
                        </Button>
                        <p className="mt-1.5 text-xs text-muted-foreground">Ortam Kütüphanesi'nden profil fotoğrafı seçin.</p>
                    </div>
                </div>

                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ad Soyad *</label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
                </div>

                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Telefon</label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="05xx xxx xx xx" data-testid="profile-phone-input" />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">E-posta (değiştirilemez)</label>
                        <Input value={me.email} disabled data-testid="profile-email" />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rol</label>
                        <Input value={ROLE_TR[me.role] || me.role} disabled data-testid="profile-role" />
                    </div>
                </div>

                {me.company && (
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bağlı Firma</label>
                        <Input value={me.company.company_name || me.company.name || ""} disabled data-testid="profile-company" />
                    </div>
                )}

                <Button onClick={save} disabled={busy} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="profile-save-button">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Kaydet
                </Button>
            </div>

            {contact && (
                <div className="space-y-4 rounded-2xl border bg-card p-6 shadow-sm" data-testid="profile-contact-card">
                    <div>
                        <h3 className="font-display text-lg font-bold">İletişim Sayfası Firma Bilgileri</h3>
                        <p className="mt-1 text-sm text-muted-foreground">İletişim sayfasında ziyaretçilere görünen bilgileri buradan güncelleyin.</p>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Adres</label>
                        <Input value={contact.contact_address} onChange={(e) => setContact({ ...contact, contact_address: e.target.value })} data-testid="profile-contact-address" />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Telefon</label>
                            <Input value={contact.contact_phone} onChange={(e) => setContact({ ...contact, contact_phone: e.target.value })} placeholder="05xx xxx xx xx" data-testid="profile-contact-phone" />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">E-posta</label>
                            <Input value={contact.contact_email} onChange={(e) => setContact({ ...contact, contact_email: e.target.value })} data-testid="profile-contact-email" />
                        </div>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">WhatsApp Hattı</label>
                        <Input value={contact.site_whatsapp} onChange={(e) => setContact({ ...contact, site_whatsapp: e.target.value })} placeholder="905432382407" data-testid="profile-contact-whatsapp" />
                        <p className="mt-1 text-xs text-muted-foreground">90 ile başlayan 12 haneli format (örn: 905432382407). Bu numara ATA AI sohbet penceresindeki WhatsApp butonunda da kullanılır.</p>
                    </div>
                    <Button onClick={saveContact} disabled={busyC} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="profile-contact-save">
                        {busyC ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        İletişim Bilgilerini Kaydet
                    </Button>
                </div>
            )}

            <MediaPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={(url) => {
                    setForm({ ...form, picture: url });
                    setPickerOpen(false);
                }}
            />
        </div>
    );
}
